import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import arrayMove from 'array-move';
import { adminProductApi, mapBulkCreateProductItem } from '../../../../../api/adminProductApi';
import { fetchCurrencyRates } from '../../../../../api/settingsApi';
import { extractApiArray, getApiErrorText } from '../../../../../utils/apiResponse';
import { parseExchangeRate } from '../../../../../utils/productPrice';
import { PRODUCT_STATES } from '../../constants';
import { FieldLabel, HintTip } from './AdminFormHints';
import BulkCreateManualTable from './BulkCreateManualTable';
import BulkCreateProductsTable from './BulkCreateProductsTable';
import CategoryBadgePicker from './CategoryBadgePicker';
import {
  buildBulkCreateItemsFromRows,
  buildColumnHeaders,
  getDefaultColumnIndices,
  normalizeParseRowsInput,
  normalizeSkipRowsInput,
  selectInputOnFocus
} from './bulkCreateExcelUtils';
import {
  applyColumnPaste,
  applyDiscountGlobalsToItems,
  applyManualFieldValue,
  createEmptyManualItem,
  createInitialManualItems,
  isBulkCreateRowValid,
  isManualRowEmpty,
  itemNeedsActiveCurrencyRate,
  normalizeItemForPriceCurrency,
  refreshManualItemsUah
} from './bulkCreateManualUtils';
import {
  DEFAULT_COL,
  DEFAULT_PARSE_ROWS,
  DEFAULT_SKIP_ROWS,
  ENTRY_MODE_FILE,
  ENTRY_MODE_MANUAL,
  getPriceCurrencyOption,
  MAX_PREVIEW_ROWS,
  PRICE_CURRENCY_OPTIONS,
  PRICE_CURRENCY_POUNDS,
  PRICE_CURRENCY_UAH,
  UNMAPPED
} from './constants';

const BulkCreateProductsModal = ({
  show,
  onClose,
  categories = [],
  onCreated
}) => {
  const [entryMode, setEntryMode] = useState(ENTRY_MODE_MANUAL);
  const [fileName, setFileName] = useState('');
  const [workbook, setWorkbook] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [sheetName, setSheetName] = useState('');
  const [rows, setRows] = useState([]);
  const [maxCols, setMaxCols] = useState(0);
  const [skipRows, setSkipRows] = useState(DEFAULT_SKIP_ROWS);
  const [parseRows, setParseRows] = useState(String(DEFAULT_PARSE_ROWS));
  const [nameColIdx, setNameColIdx] = useState(String(DEFAULT_COL.name));
  const [articleColIdx, setArticleColIdx] = useState(String(DEFAULT_COL.article));
  const [barcodeColIdx, setBarcodeColIdx] = useState(String(DEFAULT_COL.barcode));
  const [priceColIdx, setPriceColIdx] = useState(String(DEFAULT_COL.price));
  const [quantityColIdx, setQuantityColIdx] = useState(String(DEFAULT_COL.quantity));
  const [parseError, setParseError] = useState('');
  const [pasteWarning, setPasteWarning] = useState('');
  const [items, setItems] = useState(() => createInitialManualItems(1));
  const [categoryMode, setCategoryMode] = useState('all');
  const [globalCategoryIds, setGlobalCategoryIds] = useState([]);
  const [globalDiscountCategory, setGlobalDiscountCategory] = useState('');
  const [globalDiscount, setGlobalDiscount] = useState('0');
  const [globalExpectedArrivalDate, setGlobalExpectedArrivalDate] = useState('');
  const [priceCurrency, setPriceCurrency] = useState(PRICE_CURRENCY_POUNDS);
  const [expandedCategoryRow, setExpandedCategoryRow] = useState(null);
  const [defaultState, setDefaultState] = useState('ACTIVE');
  const [submitState, setSubmitState] = useState({ loading: false, error: '', result: null });
  const [discountCategories, setDiscountCategories] = useState([]);
  const [poundRateRaw, setPoundRateRaw] = useState('');
  const [dollarRateRaw, setDollarRateRaw] = useState('');
  const [euroRateRaw, setEuroRateRaw] = useState('');
  const [referenceDataLoading, setReferenceDataLoading] = useState(false);

  const poundRate = parseExchangeRate(poundRateRaw);
  const dollarRate = parseExchangeRate(dollarRateRaw);
  const euroRate = parseExchangeRate(euroRateRaw);
  const currencyRates = useMemo(
    () => ({ poundRate, dollarRate, euroRate }),
    [poundRate, dollarRate, euroRate]
  );

  const applyDefaultColumns = (computedMaxCols) => {
    const defaults = getDefaultColumnIndices(computedMaxCols);
    setNameColIdx(defaults.nameColIdx);
    setArticleColIdx(defaults.articleColIdx);
    setBarcodeColIdx(defaults.barcodeColIdx);
    setPriceColIdx(defaults.priceColIdx);
    setQuantityColIdx(defaults.quantityColIdx);
  };

  const discountDefaults = useMemo(() => ({
    discount: globalDiscount,
    discountCategoryName: globalDiscountCategory
  }), [globalDiscount, globalDiscountCategory]);

  const resetSharedState = useCallback(() => {
    setCategoryMode('all');
    setGlobalCategoryIds([]);
    setGlobalDiscountCategory('');
    setGlobalDiscount('0');
    setGlobalExpectedArrivalDate('');
    setPriceCurrency(PRICE_CURRENCY_POUNDS);
    setExpandedCategoryRow(null);
    setDefaultState('ACTIVE');
    setSubmitState({ loading: false, error: '', result: null });
    setParseError('');
    setPasteWarning('');
  }, []);

  const resetFileState = useCallback(() => {
    setFileName('');
    setWorkbook(null);
    setSheetNames([]);
    setSheetName('');
    setRows([]);
    setMaxCols(0);
    setSkipRows(DEFAULT_SKIP_ROWS);
    setParseRows(String(DEFAULT_PARSE_ROWS));
    setNameColIdx(String(DEFAULT_COL.name));
    setArticleColIdx(String(DEFAULT_COL.article));
    setBarcodeColIdx(String(DEFAULT_COL.barcode));
    setPriceColIdx(String(DEFAULT_COL.price));
    setQuantityColIdx(String(DEFAULT_COL.quantity));
    setItems([]);
    resetSharedState();
  }, [resetSharedState]);

  const resetManualState = useCallback(() => {
    setItems(createInitialManualItems(1, []));
    resetSharedState();
  }, [resetSharedState]);

  const resetAll = useCallback(() => {
    setEntryMode(ENTRY_MODE_MANUAL);
    resetFileState();
    setItems(createInitialManualItems(1, []));
  }, [resetFileState]);

  useEffect(() => {
    if (!show) {
      resetAll();
      setDiscountCategories([]);
      setPoundRateRaw('');
      setDollarRateRaw('');
      setEuroRateRaw('');
      setReferenceDataLoading(false);
      document.body.classList.remove('bulk-create-sorting');
    }
  }, [show, resetAll]);

  useEffect(() => {
    if (!show) return undefined;

    let cancelled = false;
    setReferenceDataLoading(true);

    Promise.all([
      adminProductApi.getDiscountCategories()
        .then(res => extractApiArray(res))
        .catch(err => {
          console.error('Failed to load discount categories', err);
          return [];
        }),
      fetchCurrencyRates()
        .then(data => ({
          pound: data?.pound_rate != null ? String(data.pound_rate) : '',
          dollar: data?.dollar_rate != null ? String(data.dollar_rate) : '',
          euro: data?.euro_rate != null ? String(data.euro_rate) : ''
        }))
        .catch(err => {
          console.error('Failed to load currency rates', err);
          return { pound: '', dollar: '', euro: '' };
        })
    ]).then(([categoriesList, rates]) => {
      if (cancelled) return;
      setDiscountCategories(categoriesList);
      setPoundRateRaw(rates.pound);
      setDollarRateRaw(rates.dollar);
      setEuroRateRaw(rates.euro);
      setReferenceDataLoading(false);
    });

    return () => { cancelled = true; };
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitState.loading) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, onClose, submitState.loading]);

  const switchEntryMode = (mode) => {
    if (mode === entryMode || submitState.loading) return;
    setPasteWarning('');
    setParseError('');
    setSubmitState({ loading: false, error: '', result: null });
    setExpandedCategoryRow(null);
    if (mode === ENTRY_MODE_MANUAL) {
      setFileName('');
      setWorkbook(null);
      setSheetNames([]);
      setSheetName('');
      setRows([]);
      setMaxCols(0);
      setItems(createInitialManualItems(1, categoryMode === 'all' ? globalCategoryIds : [], discountDefaults));
    } else {
      setItems([]);
    }
    setEntryMode(mode);
  };

  const parseSheet = (wb, name) => {
    if (!wb || !name) return;
    const ws = wb.Sheets[name];
    if (!ws) {
      setParseError('Не вдалося знайти вибраний аркуш у файлі.');
      return;
    }
    const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '', raw: false });
    const trimmed = (aoa || []).filter(r => Array.isArray(r) && r.some(c => String(c).trim() !== ''));
    if (!trimmed.length) {
      setRows([]);
      setMaxCols(0);
      setItems([]);
      setParseError('Аркуш порожній або не містить даних.');
      return;
    }
    setRows(trimmed);
    const computedMaxCols = Math.max(...trimmed.map(r => r.length));
    setMaxCols(computedMaxCols);
    applyDefaultColumns(computedMaxCols);
    setParseError('');
    setSubmitState({ loading: false, error: '', result: null });
  };

  const parseExcel = async (file) => {
    setParseError('');
    setSubmitState({ loading: false, error: '', result: null });
    setItems([]);
    if (!file) return;
    setFileName(file.name || '');
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array', cellDates: true });
    setWorkbook(wb);
    const names = wb.SheetNames || [];
    setSheetNames(names);
    const firstSheetName = names.length ? names[0] : '';
    if (!firstSheetName) {
      setParseError('Не знайдено жодного аркуша в файлі.');
      return;
    }
    setSheetName(firstSheetName);
    parseSheet(wb, firstSheetName);
  };

  const headers = useMemo(
    () => buildColumnHeaders(rows, maxCols, skipRows),
    [rows, maxCols, skipRows]
  );

  const columnOptions = useMemo(() => {
    const none = [{ value: UNMAPPED, label: '—' }];
    const mapped = headers.map((h, idx) => ({ value: String(idx), label: h }));
    return [...none, ...mapped];
  }, [headers]);

  const buildItemsFromRows = useCallback(() => buildBulkCreateItemsFromRows({
    rows,
    maxCols,
    skipRows,
    parseRows,
    nameColIdx,
    articleColIdx,
    barcodeColIdx,
    priceColIdx,
    quantityColIdx,
    poundRate
  }), [
    rows, maxCols, skipRows, parseRows, nameColIdx, articleColIdx, barcodeColIdx, priceColIdx,
    quantityColIdx, poundRate
  ]);

  useEffect(() => {
    if (entryMode !== ENTRY_MODE_FILE) return;
    if (!rows.length) {
      setItems([]);
      return;
    }
    setItems(prev => {
      const built = buildItemsFromRows();
      const prevByKey = new Map(prev.map(it => [it.key, it]));
      const usedKeys = new Set();
      const merged = [];

      for (const prevItem of prev) {
        const match = built.find(b => b.key === prevItem.key);
        if (!match) continue;
        usedKeys.add(match.key);
        merged.push({
          ...match,
          selected: prevItem.selected !== false,
          categoryIds: prevItem.categoryIds || [],
          discount: prevItem.discount != null ? prevItem.discount : (globalDiscount === '' ? '0' : globalDiscount),
          discountCategoryName: prevItem.discountCategoryName != null
            ? prevItem.discountCategoryName
            : (globalDiscountCategory || '')
        });
      }

      for (const it of built) {
        if (usedKeys.has(it.key)) continue;
        merged.push({
          ...it,
          selected: true,
          categoryIds: [],
          discount: globalDiscount === '' ? '0' : globalDiscount,
          discountCategoryName: globalDiscountCategory || ''
        });
      }

      return merged;
    });
  }, [entryMode, rows, buildItemsFromRows, globalDiscount, globalDiscountCategory]);

  useEffect(() => {
    if (categoryMode !== 'all') return;
    setItems(prev => prev.map(it => ({ ...it, categoryIds: [...globalCategoryIds] })));
  }, [globalCategoryIds, categoryMode]);

  useEffect(() => {
    setItems(prev => applyDiscountGlobalsToItems(prev, globalDiscount, globalDiscountCategory));
  }, [globalDiscount, globalDiscountCategory]);

  useEffect(() => {
    if (entryMode !== ENTRY_MODE_MANUAL) return;
    if (referenceDataLoading) return;
    setItems(prev => {
      const next = refreshManualItemsUah(prev, currencyRates, priceCurrency);
      return next.some((it, i) => it !== prev[i]) ? next : prev;
    });
  }, [entryMode, referenceDataLoading, currencyRates, priceCurrency]);

  const onSortEnd = useCallback(({ oldIndex, newIndex }) => {
    if (submitState.loading || oldIndex === newIndex) return;
    setItems(prev => arrayMove(prev, oldIndex, newIndex));
    setExpandedCategoryRow(null);
  }, [submitState.loading]);

  const toggleGlobalCategory = (catId) => {
    setGlobalCategoryIds(prev => (prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]));
  };

  const toggleItemCategory = (index, catId) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== index) return it;
      const ids = it.categoryIds || [];
      const next = ids.includes(catId) ? ids.filter(id => id !== catId) : [...ids, catId];
      return { ...it, categoryIds: next };
    }));
  };

  const toggleItemSelected = (index) => {
    setItems(prev => prev.map((it, i) => (i === index ? { ...it, selected: it.selected === false } : it)));
  };

  const toggleAllSelected = () => {
    const selectable = items.filter(it => !isManualRowEmpty(it));
    if (!selectable.length) return;
    const allSelected = selectable.every(it => it.selected !== false);
    const nextSelected = !allSelected;
    const keys = new Set(selectable.map(it => it.key));
    setItems(prev => prev.map(it => (keys.has(it.key) ? { ...it, selected: nextSelected } : it)));
  };

  const handleManualFieldChange = (index, field, value) => {
    setPasteWarning('');
    setItems(prev => prev.map((it, i) => (
      i === index ? applyManualFieldValue(it, field, value, currencyRates, priceCurrency) : it
    )));
  };

  const handleManualPaste = (e, index, field) => {
    const text = e.clipboardData?.getData('text');
    if (text == null || !/\r|\n|\u2028|\u2029/.test(text)) return;
    e.preventDefault();
    setPasteWarning('');
    const categoryIds = categoryMode === 'all' ? globalCategoryIds : [];
    setItems(prev => {
      const { items: next } = applyColumnPaste(
        prev,
        index,
        field,
        text,
        currencyRates,
        categoryIds,
        priceCurrency,
        discountDefaults
      );
      return refreshManualItemsUah(next, currencyRates, priceCurrency);
    });
  };

  const handleAddManualRow = () => {
    setPasteWarning('');
    setItems(prev => [
      ...prev,
      createEmptyManualItem(categoryMode === 'all' ? globalCategoryIds : [], discountDefaults)
    ]);
  };

  const handleRemoveManualRow = (index) => {
    setPasteWarning('');
    setItems(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.length
        ? next
        : createInitialManualItems(1, categoryMode === 'all' ? globalCategoryIds : [], discountDefaults);
    });
    setExpandedCategoryRow(null);
  };

  const handleRowDiscountChange = (index, field, value) => {
    if (field === 'discount' && value !== '' && !/^\d+$/.test(value)) return;
    setItems(prev => prev.map((it, i) => (i === index ? { ...it, [field]: value } : it)));
  };

  const validItems = items.filter(it => it.selected !== false && isBulkCreateRowValid(it));
  const validCount = validItems.length;
  const totalValid = items.filter(isBulkCreateRowValid).length;
  const selectableItems = items.filter(it => !isManualRowEmpty(it));
  const selectableCount = selectableItems.length;
  const selectedSelectableCount = selectableItems.filter(it => it.selected !== false).length;
  const allSelectableSelected = selectableCount > 0 && selectedSelectableCount === selectableCount;
  const someSelectableSelected = selectedSelectableCount > 0 && selectedSelectableCount < selectableCount;
  const incompleteSelectedCount = items.filter(
    it => it.selected !== false && !isManualRowEmpty(it) && !isBulkCreateRowValid(it)
  ).length;

  const needsActiveRate = entryMode === ENTRY_MODE_MANUAL
    && priceCurrency !== PRICE_CURRENCY_UAH
    && validItems.some(it => itemNeedsActiveCurrencyRate(it, priceCurrency));
  const activeCurrencyOpt = getPriceCurrencyOption(priceCurrency);
  const activeRate = activeCurrencyOpt.rateKey ? currencyRates[activeCurrencyOpt.rateKey] : null;
  const hasActiveRate = activeRate != null && activeRate > 0;

  const needsPoundRate = entryMode === ENTRY_MODE_FILE
    && validItems.some(it => itemNeedsActiveCurrencyRate(it, PRICE_CURRENCY_POUNDS));
  const hasValidPoundRate = poundRate != null && poundRate > 0;

  const fileMappingOk = entryMode !== ENTRY_MODE_FILE
    || (nameColIdx !== UNMAPPED && articleColIdx !== UNMAPPED);

  const canSubmit = validCount > 0
    && fileMappingOk
    && !referenceDataLoading
    && (entryMode === ENTRY_MODE_MANUAL
      ? (!needsActiveRate || hasActiveRate)
      : (!needsPoundRate || hasValidPoundRate));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const payload = [...validItems].reverse().map(it => mapBulkCreateProductItem({
      ...(entryMode === ENTRY_MODE_MANUAL ? normalizeItemForPriceCurrency(it, priceCurrency) : it),
      state: defaultState,
      categoryIds: categoryMode === 'all' ? globalCategoryIds : (it.categoryIds || []),
      discount: it.discount != null && it.discount !== '' ? it.discount : 0,
      discountCategoryName: it.discountCategoryName || null,
      expectedArrivalDate: globalExpectedArrivalDate || null
    }));

    if (!payload.length) {
      setSubmitState({ loading: false, error: 'Немає валідних рядків для створення', result: null });
      return;
    }

    setSubmitState({ loading: true, error: '', result: null });
    try {
      const res = await adminProductApi.bulkCreateProducts(payload);
      const result = res.data?.data ?? res.data;
      const created = result?.created ?? 0;
      const errors = result?.errors || [];

      if (created === 0) {
        const detail = errors.slice(0, 5).map(e => `${e.productCode || '—'}: ${e.reason}`).join('\n');
        setSubmitState({
          loading: false,
          error: detail || 'Жоден товар не створено',
          result: null
        });
        return;
      }

      if (onCreated) onCreated(result);
      onClose();
    } catch (e) {
      setSubmitState({ loading: false, error: getApiErrorText(e, 'Помилка створення товарів.'), result: null });
    }
  };

  const formatRateLabel = (rate) => (referenceDataLoading
    ? '…'
    : (rate != null && rate > 0 ? rate : '—'));

  if (!show) return null;

  const previewItems = items.slice(0, MAX_PREVIEW_ROWS);
  const showGlobals = entryMode === ENTRY_MODE_MANUAL || rows.length > 0;

  const mappingFields = [
    { key: 'name', label: 'Назва', value: nameColIdx, set: setNameColIdx, required: true, allowNone: false },
    { key: 'article', label: 'Артикул', value: articleColIdx, set: setArticleColIdx, required: true, allowNone: false },
    { key: 'barcode', label: 'Штрихкод', value: barcodeColIdx, set: setBarcodeColIdx, allowNone: true },
    { key: 'price', label: 'Ціна £', value: priceColIdx, set: setPriceColIdx, allowNone: true },
    { key: 'qty', label: 'Кількість', value: quantityColIdx, set: setQuantityColIdx, allowNone: true }
  ];

  const renderGlobalsBar = () => (
    <div
      className="d-flex flex-wrap align-items-end mb-3 p-2 rounded border bg-light"
      style={{ gap: '0.75rem 1rem' }}
    >
      {entryMode === ENTRY_MODE_FILE ? (
        <>
          <div style={{ width: '72px', flexShrink: 0 }}>
            <FieldLabel hint="Скільки рядків пропустити зверху файлу (заголовки)" hintId="bulk-create-skip-hint">Пропуск</FieldLabel>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-control form-control-sm"
              value={skipRows}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || /^\d+$/.test(v)) setSkipRows(v);
              }}
              onFocus={selectInputOnFocus}
              onBlur={() => setSkipRows(prev => normalizeSkipRowsInput(prev))}
              disabled={submitState.loading}
            />
          </div>
          <div style={{ width: '88px', flexShrink: 0 }}>
            <FieldLabel hint="Скільки рядків даних показати з файлу. За замовчуванням до ліміту" hintId="bulk-create-limit-hint">Ліміт</FieldLabel>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-control form-control-sm"
              value={parseRows}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || /^\d+$/.test(v)) setParseRows(v);
              }}
              onFocus={selectInputOnFocus}
              onBlur={() => setParseRows(prev => normalizeParseRowsInput(prev))}
              disabled={submitState.loading}
            />
          </div>
        </>
      ) : null}
      <div style={{ width: '200px', flex: '0 0 auto' }}>
        <FieldLabel>Статус</FieldLabel>
        <select
          className="form-control form-control-sm"
          style={{ width: '100%' }}
          value={defaultState}
          onChange={e => setDefaultState(e.target.value)}
          disabled={submitState.loading}
        >
          {PRODUCT_STATES.filter(s => s.value).map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>
      <div style={{ width: '140px', flex: '0 0 auto' }}>
        <FieldLabel hint="Сетиться на всі рядки. Після цього можна змінити в кожному рядку окремо" hintId="bulk-create-discount-cat-hint">Кат. знижки</FieldLabel>
        <select
          className="form-control form-control-sm"
          style={{ width: '100%' }}
          value={globalDiscountCategory}
          onChange={e => setGlobalDiscountCategory(e.target.value)}
          disabled={submitState.loading}
        >
          <option value="">—</option>
          {discountCategories.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </div>
      <div style={{ width: '72px', flex: '0 0 auto' }}>
        <FieldLabel hint="Сетиться на всі рядки. Після цього можна змінити в кожному рядку окремо" hintId="bulk-create-discount-hint">Знижка</FieldLabel>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          className="form-control form-control-sm"
          value={globalDiscount}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '' || /^\d+$/.test(v)) setGlobalDiscount(v);
          }}
          onFocus={selectInputOnFocus}
          onBlur={() => setGlobalDiscount(prev => (prev === '' ? '0' : prev))}
          disabled={submitState.loading}
        />
      </div>
      <div style={{ width: '150px', flex: '0 0 auto' }}>
        <FieldLabel hint="Одна дата надходження для всіх нових товарів" hintId="bulk-create-arrival-hint">Надходження</FieldLabel>
        <input
          type="date"
          className="form-control form-control-sm"
          style={{ width: '100%' }}
          value={globalExpectedArrivalDate}
          onChange={e => setGlobalExpectedArrivalDate(e.target.value)}
          disabled={submitState.loading}
        />
      </div>
      {entryMode === ENTRY_MODE_MANUAL ? (
        <div style={{ width: '100px', flex: '0 0 auto' }}>
          <FieldLabel hint="Валюта ціни для всіх рядків у таблиці" hintId="bulk-create-currency-hint">Валюта</FieldLabel>
          <select
            className="form-control form-control-sm"
            style={{ width: '100%' }}
            value={priceCurrency}
            onChange={e => setPriceCurrency(e.target.value)}
            disabled={submitState.loading}
          >
            {PRICE_CURRENCY_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      ) : null}
      <div className="ml-auto d-flex flex-column align-items-end pb-1" style={{ gap: '2px', flexShrink: 0 }}>
        <HintTip id="bulk-create-rates-hint" text="Курси з налаштувань адмінки. При зміні іноземної ціни грн перераховується автоматично">
          <span className="small text-muted">
            <i className="bi bi-currency-exchange mr-1" />
            Курси
          </span>
        </HintTip>
        <div className="d-flex flex-wrap justify-content-end" style={{ gap: '0.75rem' }}>
          {entryMode === ENTRY_MODE_MANUAL && priceCurrency !== PRICE_CURRENCY_UAH ? (
            <span className="small">
              <span className="text-muted">{activeCurrencyOpt.label}</span>{' '}
              <strong>{formatRateLabel(activeRate)}</strong>
              <span className="text-muted ml-1">→ грн</span>
            </span>
          ) : (
            <>
              <span className="small"><span className="text-muted">£</span> <strong>{formatRateLabel(poundRate)}</strong></span>
              <span className="small"><span className="text-muted">$</span> <strong>{formatRateLabel(dollarRate)}</strong></span>
              <span className="small"><span className="text-muted">€</span> <strong>{formatRateLabel(euroRate)}</strong></span>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderCategoriesBlock = () => (
    <div className="mb-3">
      <div className="d-flex align-items-center mb-2 flex-wrap" style={{ gap: '0.5rem' }}>
        <span className="font-weight-bold">Категорії товарів</span>
        <div className="btn-group btn-group-sm">
          <button
            type="button"
            className={`btn ${categoryMode === 'all' ? 'btn-dark' : 'btn-outline-secondary'}`}
            onClick={() => setCategoryMode('all')}
            disabled={submitState.loading}
          >
            Для всіх
          </button>
          <button
            type="button"
            className={`btn ${categoryMode === 'each' ? 'btn-dark' : 'btn-outline-secondary'}`}
            onClick={() => {
              setCategoryMode('each');
              setItems(prev => prev.map(it => ({ ...it, categoryIds: it.categoryIds?.length ? it.categoryIds : [...globalCategoryIds] })));
            }}
            disabled={submitState.loading}
          >
            Окремо
          </button>
        </div>
        {categoryMode === 'each' ? (
          <HintTip id="bulk-create-categories-each-hint" text="Оберіть категорії в колонці таблиці нижче">
            <span className="small text-muted">
              <i className="bi bi-info-circle mr-1" />
              у таблиці
            </span>
          </HintTip>
        ) : null}
      </div>
      {categoryMode === 'all' ? (
        <CategoryBadgePicker
          categories={categories}
          selectedIds={globalCategoryIds}
          onToggle={toggleGlobalCategory}
        />
      ) : null}
    </div>
  );

  const renderOrderHeader = () => (
    <div className="d-flex align-items-center mb-2 flex-wrap" style={{ gap: '0.5rem' }}>
      <span className="font-weight-bold">
        Порядок на сайті
        <HintTip id="bulk-create-site-order-hint" text="Як у таблиці — так на сайті. Зверху створюється останнім і з’являється першим у каталозі">
          <i className="bi bi-info-circle text-muted ml-1" style={{ fontSize: '0.85rem' }} />
        </HintTip>
        <span className="text-muted font-weight-normal ml-1">
          ({validCount}{totalValid !== validCount ? ` з ${totalValid}` : ''})
        </span>
      </span>
      {incompleteSelectedCount > 0 ? (
        <span className="small text-warning">
          У {incompleteSelectedCount} обраних рядках немає назви або артикулу
        </span>
      ) : null}
      {needsPoundRate && !hasValidPoundRate && !referenceDataLoading ? (
        <span className="small text-danger">Потрібен курс £ для розрахунку ціни в грн</span>
      ) : null}
      {needsActiveRate && !hasActiveRate && !referenceDataLoading ? (
        <span className="small text-danger">
          Потрібен курс {activeCurrencyOpt.label} для розрахунку ціни в грн
        </span>
      ) : null}
      {entryMode === ENTRY_MODE_FILE && items.length > MAX_PREVIEW_ROWS ? (
        <span className="small text-muted">перші {MAX_PREVIEW_ROWS}</span>
      ) : null}
      <label
        className="small mb-0 d-inline-flex align-items-center ml-auto"
        style={{ gap: '6px', cursor: selectableCount ? 'pointer' : 'not-allowed', userSelect: 'none' }}
      >
        <input
          type="checkbox"
          checked={allSelectableSelected}
          ref={(el) => {
            if (el) el.indeterminate = someSelectableSelected;
          }}
          onChange={toggleAllSelected}
          disabled={submitState.loading || selectableCount === 0}
          style={{ width: '16px', height: '16px', cursor: selectableCount ? 'pointer' : 'not-allowed' }}
        />
        Обрати всі
      </label>
    </div>
  );

  return (
    <>
      <div
        className="modal-backdrop show"
        style={{ zIndex: 1040 }}
        onClick={() => !submitState.loading && onClose()}
      />
      <div
        className="modal show"
        tabIndex="-1"
        role="dialog"
        style={{ display: 'block', zIndex: 1050 }}
        onClick={() => !submitState.loading && onClose()}
      >
        <div
          className="modal-dialog modal-dialog-scrollable"
          role="document"
          style={{ maxWidth: 'min(96vw, 1480px)', width: '96vw' }}
          onClick={e => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header py-2">
              <h5 className="modal-title mb-0">
                <i className="bi bi-plus-square mr-2" />
                Масове додавання товарів
              </h5>
              <button type="button" className="close" aria-label="Close" onClick={() => !submitState.loading && onClose()}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body text-left pt-3">
              <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap" style={{ gap: '0.5rem' }}>
                <div className="btn-group btn-group-sm">
                  <button
                    type="button"
                    className={`btn ${entryMode === ENTRY_MODE_MANUAL ? 'btn-dark' : 'btn-outline-secondary'}`}
                    onClick={() => switchEntryMode(ENTRY_MODE_MANUAL)}
                    disabled={submitState.loading}
                  >
                    <i className="bi bi-table mr-1" />
                    Вручну
                  </button>
                  <button
                    type="button"
                    className={`btn ${entryMode === ENTRY_MODE_FILE ? 'btn-dark' : 'btn-outline-secondary'}`}
                    onClick={() => switchEntryMode(ENTRY_MODE_FILE)}
                    disabled={submitState.loading}
                  >
                    <i className="bi bi-file-earmark-spreadsheet mr-1" />
                    З файлу
                  </button>
                </div>
                {entryMode === ENTRY_MODE_MANUAL ? (
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetManualState} disabled={submitState.loading}>
                    Очистити таблицю
                  </button>
                ) : null}
                {entryMode === ENTRY_MODE_FILE && rows.length > 0 ? (
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetFileState} disabled={submitState.loading}>
                    Очистити
                  </button>
                ) : null}
              </div>

              {entryMode === ENTRY_MODE_FILE ? (
                <div className="d-flex align-items-center mb-3 flex-wrap" style={{ gap: '0.5rem' }}>
                  <label className="btn btn-outline-secondary btn-sm mb-0">
                    <i className="bi bi-upload mr-1" />
                    Обрати файл
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      className="d-none"
                      onChange={(e) => {
                        parseExcel(e.target.files && e.target.files[0]);
                        e.target.value = '';
                      }}
                      disabled={submitState.loading}
                    />
                  </label>
                  {fileName ? (
                    <span className="small text-muted text-truncate" style={{ maxWidth: '320px' }} title={fileName}>
                      {fileName}
                    </span>
                  ) : (
                    <span className="small text-muted">Excel або CSV</span>
                  )}
                </div>
              ) : (
                <div className="alert alert-light border py-2 mb-3 small">
                  <i className="bi bi-clipboard mr-1" />
                  Вставте багаторядковий список у назву / опис / артикул / посилання тощо — з’являться нові рядки.
                  Повний багаторядковий опис одного товару — іконкою розгортання в комірці «Опис».
                </div>
              )}

              {entryMode === ENTRY_MODE_FILE && sheetName ? (
                <div className="alert alert-light border py-2 mb-3 small">
                  <i className="bi bi-table mr-1" />
                  Аркуш: <strong>{sheetName}</strong>
                  {sheetNames.length > 1 ? (
                    <select
                      className="form-control form-control-sm d-inline-block ml-2"
                      style={{ width: 'auto', maxWidth: '240px' }}
                      value={sheetName}
                      onChange={(e) => {
                        const next = e.target.value;
                        setSheetName(next);
                        parseSheet(workbook, next);
                      }}
                      disabled={submitState.loading}
                    >
                      {sheetNames.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  ) : null}
                </div>
              ) : null}

              {showGlobals ? (
                <>
                  {renderGlobalsBar()}

                  {entryMode === ENTRY_MODE_FILE ? (
                    <>
                      <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap" style={{ gap: '0.5rem' }}>
                        <div className="font-weight-bold">
                          Змапте колонки
                          <HintTip
                            id="bulk-create-mapping-hint"
                            text="Дефолт: G — назва, F — артикул, J — штрихкод, P — ціна £, Q — кількість"
                          >
                            <i className="bi bi-info-circle text-muted ml-1" style={{ fontSize: '0.85rem' }} />
                          </HintTip>
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => applyDefaultColumns(maxCols)}
                          disabled={submitState.loading}
                        >
                          G, F, J, P, Q
                        </button>
                      </div>

                      <div className="d-flex flex-wrap mb-3" style={{ gap: '0.5rem' }}>
                        {mappingFields.map(f => (
                          <div key={f.key} style={{ minWidth: '130px', flex: '1 1 130px', maxWidth: '180px' }}>
                            <FieldLabel required={f.required}>{f.label}</FieldLabel>
                            <select
                              className="form-control form-control-sm"
                              value={String(f.value)}
                              onChange={e => f.set(e.target.value)}
                              disabled={submitState.loading}
                            >
                              {(f.allowNone ? columnOptions : columnOptions.filter(o => o.value !== UNMAPPED)).map(o => (
                                <option key={`${f.key}-${o.value}`} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}

                  {renderCategoriesBlock()}
                  {renderOrderHeader()}

                  {entryMode === ENTRY_MODE_MANUAL ? (
                    <>
                      <BulkCreateManualTable
                        items={items}
                        categoryMode={categoryMode}
                        loading={submitState.loading}
                        priceCurrency={priceCurrency}
                        discountCategories={discountCategories}
                        expandedCategoryRow={expandedCategoryRow}
                        setExpandedCategoryRow={setExpandedCategoryRow}
                        toggleItemSelected={toggleItemSelected}
                        toggleItemCategory={toggleItemCategory}
                        rowCategories={categories}
                        onSortEnd={onSortEnd}
                        onChangeField={handleManualFieldChange}
                        onPasteField={handleManualPaste}
                        onChangeDiscount={handleRowDiscountChange}
                        onRemoveRow={handleRemoveManualRow}
                      />
                      <div className="mt-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={handleAddManualRow}
                          disabled={submitState.loading}
                        >
                          <i className="bi bi-plus mr-1" />
                          Додати рядок
                        </button>
                      </div>
                    </>
                  ) : (
                    <BulkCreateProductsTable
                      previewItems={previewItems}
                      categoryMode={categoryMode}
                      loading={submitState.loading}
                      discountCategories={discountCategories}
                      expandedCategoryRow={expandedCategoryRow}
                      setExpandedCategoryRow={setExpandedCategoryRow}
                      toggleItemSelected={toggleItemSelected}
                      toggleItemCategory={toggleItemCategory}
                      rowCategories={categories}
                      onChangeDiscount={handleRowDiscountChange}
                      onSortEnd={onSortEnd}
                    />
                  )}
                </>
              ) : null}

              {pasteWarning ? <div className="alert alert-warning mt-3 mb-0">{pasteWarning}</div> : null}
              {parseError ? <div className="alert alert-danger mt-3 mb-0">{parseError}</div> : null}
              {submitState.error ? (
                <div className="alert alert-danger mt-3 mb-0" style={{ whiteSpace: 'pre-line' }}>
                  {submitState.error}
                </div>
              ) : null}
            </div>
            <div className="modal-footer py-2">
              <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={submitState.loading}>
                Закрити
              </button>
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={handleSubmit}
                disabled={!canSubmit || submitState.loading}
              >
                {submitState.loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" />
                    Створюю…
                  </>
                ) : (
                  <>Створити товари ({validCount})</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BulkCreateProductsModal;
